/**
 * Authentication Provider
 *
 * Wires React Admin auth to corporate single sign-on:
 * - `login` sends the user to the brand's hosted sign-in page or the shared
 *   access portal (there is no in-app credential form).
 * - The session (token + account record) is established at boot by the silent
 *   sign-on check (see main.tsx / auth/sso.ts); identity, role and plan-module
 *   gating read the same storage as before.
 * - `logout` ends the session at the identity provider.
 * - 401/403 responses, or any response carrying action 'logoutPage',
 *   terminate the local session.
 *
 * The web panel is for admin / client / organization / organization-manager
 * roles; the backend itself rejects staff logins on the web channel.
 */

import { AuthProvider, UserIdentity } from 'react-admin';
import {
  clearAuthData,
  getToken,
  getCurrentUser,
  getCurrentRole,
  resolveRoleName,
} from '@/utils/auth';
import { signIn, signOut } from '@/auth/sso';

export const authProvider: AuthProvider = {
  login: async (): Promise<void> => {
    // No in-app credentials: hand off to the identity provider / access portal.
    // The returned promise never resolves because a full-page redirect follows.
    signIn();
    return new Promise<void>(() => {});
  },

  logout: async (): Promise<void> => {
    clearAuthData();
    sessionStorage.clear();
    await signOut();
  },

  checkAuth: async (): Promise<void> => {
    if (!getToken()) {
      throw new Error('No token found');
    }
  },

  checkError: async (error): Promise<void> => {
    const action = error?.data?.action ?? error?.body?.action;

    // The API answers 401 'not_authorized' for plain permission denials too;
    // only explicit logout actions (invalid/superseded token) end the session.
    if (action === 'logoutPage' || action === 'logout') {
      clearAuthData();
      sessionStorage.clear();

      return Promise.reject({
        message: 'Su sesión ha expirado',
        redirectTo: '/login',
        logoutUser: true,
      });
    }

    return Promise.resolve();
  },

  getIdentity: async (): Promise<UserIdentity> => {
    const user = getCurrentUser();

    if (!user) {
      throw new Error('No user found');
    }

    return {
      id: user.user_id,
      fullName: user.full_name,
      avatar: undefined,
      email: user.email,
      role: resolveRoleName(user),
      roleId: user.role_id,
      organizationId: user.organization_id ?? null,
      clientId: user.client_id ?? null,
    } as UserIdentity & {
      email: string;
      role: string;
      roleId: number;
      organizationId: number | null;
      clientId: number | null;
    };
  },

  getPermissions: async (): Promise<string> => {
    const role = getCurrentRole();

    if (!role) {
      throw new Error('No role found');
    }

    return role;
  },
};

export default authProvider;
